import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Row } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../../api/entityType";
import useUserPreferences from "../../../hooks/useUserPreferences";
import { transformLogInput } from "../../../store/inventory/inventoryActions";
import {
  clearOperationResult,
  fetchReport,
  resetReportState,
} from "../../../store/report/reportActions";
import PermissionWrapper from "../../navigation/PermissionWrapper";
import ReportPanel from "../../shared/ReportPanel";
import AgencySelectEx from "../../shared/form/AgencySelectEx";
import {
  dateRangeConfigs,
  fixedDateRanges,
} from "../../shared/form/fixedDateRanges";
import { getInitialAgency } from "../../shared/form/getInitialAgency";
import AdvancedRangePicker from "../../shared/form/reactHookForm/AdvancedRangePicker";
import CustomInput from "../../shared/form/reactHookForm/CustomInput";
import CustomRadioGroup from "../../shared/form/reactHookForm/CustomRadioGroup";
import { toEndOfDay, toStartOfDay } from "../../shared/formatting";
import openNotificationOperationResult from "../../shared/openNotificationOperationResult";
import { ViewInventory } from "../../shared/securityRoleTasks";
import { agenciesListEqual } from "../../shared/sorting";
import AllocatedToAgentSelect from "./AllocatedToAgentSelect";
import intentoryLogReportSelectSeriesType from "./intentoryLogReportSelectSeriesType";
import { inventoryLogReportSchema } from "./reportSchemas";

// Constants
const partialInitialValues = {
  AgencyKey: "",
  FixedDateRange: fixedDateRanges.MTD,
  AllocatedToAgentKey: 0,
  SelectSeriesOption: intentoryLogReportSelectSeriesType.SeriesActivityDates,
  TransactionDate: ["", ""],
};

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 17 },
};

const InventoryLogReport = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    trigger,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(inventoryLogReportSchema),
  });

  const agencyKey = watch("AgencyKey");
  const selectSeriesOption = watch("SelectSeriesOption");

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const { preferences } = useUserPreferences(["UseYesterdayInToDateShortcuts"]);
  const toDateEndingIsYesterday = preferences?.UseYesterdayInToDateShortcuts;
  const reportOperationResult = useSelector(
    (state) => state.report.operationResult
  );

  //
  useEffect(() => {
    if (
      toDateEndingIsYesterday === null ||
      agencies == null ||
      isEmpty(agencies) ||
      agenciesListEqual(currentAgencies, agencies)
    ) {
      return;
    }

    setCurrentAgencies(agencies);
    const initialAgency = getInitialAgency(homeAgency, agencies);
    const endDate = toDateEndingIsYesterday
      ? dayjs().subtract(1, "day")
      : dayjs();

    const fullInitialValues = {
      ...partialInitialValues,
      AgencyKey: initialAgency,
      TransactionDate: [
        toStartOfDay(dayjs().startOf("month")),
        toEndOfDay(endDate.endOf("day")),
      ],
    };

    setInitialDefaultValues(cloneDeep(fullInitialValues));
    reset(fullInitialValues);
    trigger();
  }, [agencies, homeAgency, toDateEndingIsYesterday]);

  useEffect(() => {
    openNotificationOperationResult(reportOperationResult, () =>
      dispatch(clearOperationResult())
    );
  }, [reportOperationResult, dispatch]);

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformLogInput(values),
        entityType.INVENTORY,
        "PrintLog",
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="InventoryReports">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewInventory}
      >
        <Row>
          <Col span={8}>
            <AgencySelectEx
              layout={layout}
              formLabel="Agency"
              name="AgencyKey"
              required={true}
              agencies={currentAgencies}
              isLoading={defaultValues.AgencyKey === ""}
              control={control}
              onSelect={(_) => {
                setValue("AllocatedToAgentKey", null);
                trigger();
              }}
            />

            <AllocatedToAgentSelect
              control={control}
              layout={layout}
              agencyKey={agencyKey}
              allowClear={true}
              includeAllOption={true}
              setValue={setValue}
            />
          </Col>
          <Col span={14}>
            <CustomRadioGroup
              control={control}
              layout={{
                labelCol: { span: 6 },
                wrapperCol: { span: 18 },
              }}
              formLabel="Select Series based on"
              name="SelectSeriesOption"
              required={true}
              afterChange={() => trigger()}
              formStyle={{ marginBottom: 0 }}
              customField1={toDateEndingIsYesterday} // Forces re-render inside memoized CustomRadioGroup
              className="vertical-radio-group"
              options={[
                {
                  value: intentoryLogReportSelectSeriesType.SeriesActivityDates,
                  label: (
                    <AdvancedRangePicker
                      formItem={{
                        style: { marginBottom: 0 },
                        label: "Activity Date",
                      }}
                      control={control}
                      name="TransactionDate"
                      setValue={setValue}
                      dateRangeConfig={dateRangeConfigs.TRANSACTION}
                      toDateEndingIsYesterday={toDateEndingIsYesterday}
                    />
                  ),
                },
                {
                  value: intentoryLogReportSelectSeriesType.Plates,
                  label: (
                    <CustomInput
                      control={control}
                      formStyle={{ marginBottom: 0 }}
                      formLabel="Plates"
                      name="SelectedPlates"
                      style={{ width: 422 }}
                      placeholder="Separate each number with a comma"
                      disabled={
                        selectSeriesOption !==
                        intentoryLogReportSelectSeriesType.Plates
                      }
                    />
                  ),
                },
                {
                  value:
                    intentoryLogReportSelectSeriesType.NotFullyIssuedSeries,
                  label: "Not Fully Issued",
                },
              ]}
            />
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default InventoryLogReport;
